import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Linking,
  Alert,
} from 'react-native';
import {
  MERCHANT_PAYMENT_ADDENDUM_SUMMARY,
  MERCHANT_PAYMENT_LINKING_LABEL,
  MERCHANT_PAYMENT_TERMS_VERSION,
  MERCHANT_TERMS_CHECKBOXES,
  MerchantTermsCheckboxId,
  REQUIRED_MERCHANT_TERMS_IDS,
} from '../constants/merchantPaymentTerms';
import {
  hasValidMerchantTermsAcceptance,
  persistMerchantTermsAcceptance,
} from '../services/merchantTermsService';
import { AuthManager } from '../utils/authManager';

export interface MerchantPaymentTermsAgreementProps {
  businessId: string;
  /** Which link flow is active; use `both` on the shared payment setup screen. */
  provider?: 'stripe' | 'paypal' | 'both';
  /** Called when every required checkbox is checked (or already accepted for current version). */
  onAllAcceptedChange: (allAccepted: boolean) => void;
  /** After acceptance is saved to Firestore/local profile */
  onPersisted?: () => void;
}

const openUrl = async (url: string) => {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
    } else {
      Alert.alert('Error', 'Cannot open link');
    }
  } catch {
    Alert.alert('Error', 'Failed to open link');
  }
};

const MerchantPaymentTermsAgreement: React.FC<MerchantPaymentTermsAgreementProps> = ({
  businessId,
  provider = 'both',
  onAllAcceptedChange,
  onPersisted,
}) => {
  const [checked, setChecked] = useState<Record<MerchantTermsCheckboxId, boolean>>(
    () =>
      REQUIRED_MERCHANT_TERMS_IDS.reduce(
        (acc, id) => {
          acc[id] = false;
          return acc;
        },
        {} as Record<MerchantTermsCheckboxId, boolean>
      )
  );
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [persistError, setPersistError] = useState<string | null>(null);
  const persistInFlight = useRef(false);

  const allChecked = useMemo(
    () => REQUIRED_MERCHANT_TERMS_IDS.every((id) => checked[id]),
    [checked]
  );

  const loadExisting = useCallback(async () => {
    const owner = await AuthManager.getSavedBusinessOwner();
    if (owner?.id !== businessId) {
      setAlreadyAccepted(false);
      onAllAcceptedChange(false);
      return;
    }
    if (hasValidMerchantTermsAcceptance(owner)) {
      setAlreadyAccepted(true);
      setAcceptedAt(owner.merchantPaymentTermsAcceptance?.acceptedAt || null);
      const ids = owner.merchantPaymentTermsAcceptance?.acceptedItemIds || [];
      setChecked(
        REQUIRED_MERCHANT_TERMS_IDS.reduce(
          (acc, id) => {
            acc[id] = ids.includes(id);
            return acc;
          },
          {} as Record<MerchantTermsCheckboxId, boolean>
        )
      );
      onAllAcceptedChange(true);
    } else {
      setAlreadyAccepted(false);
      onAllAcceptedChange(false);
    }
  }, [businessId, onAllAcceptedChange]);

  useEffect(() => {
    loadExisting();
  }, [loadExisting]);

  useEffect(() => {
    if (alreadyAccepted) {
      onAllAcceptedChange(true);
      return;
    }
    onAllAcceptedChange(allChecked);
  }, [allChecked, alreadyAccepted, onAllAcceptedChange]);

  useEffect(() => {
    if (!allChecked || alreadyAccepted || !businessId || persistInFlight.current) {
      return;
    }

    let cancelled = false;
    persistInFlight.current = true;
    (async () => {
      setSaving(true);
      setPersistError(null);
      try {
        const ids = REQUIRED_MERCHANT_TERMS_IDS.filter((id) => checked[id]);
        await persistMerchantTermsAcceptance(businessId, ids, provider);
        if (!cancelled) {
          setAlreadyAccepted(true);
          setAcceptedAt(new Date().toISOString());
          onPersisted?.();
        }
      } catch (e: any) {
        if (!cancelled) {
          setPersistError(e?.message || 'Could not save acceptance');
        }
      } finally {
        persistInFlight.current = false;
        if (!cancelled) {
          setSaving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allChecked, alreadyAccepted, businessId, checked, onPersisted, provider]);

  const toggle = (id: MerchantTermsCheckboxId) => {
    if (alreadyAccepted) {
      return;
    }
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    setPersistError(null);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Merchant payment terms</Text>
      <Text style={styles.version}>
        Version {MERCHANT_PAYMENT_TERMS_VERSION} — required before linking{' '}
        {provider === 'both'
          ? MERCHANT_PAYMENT_LINKING_LABEL
          : provider === 'paypal'
            ? 'PayPal'
            : 'Stripe'}
      </Text>

      {alreadyAccepted && acceptedAt ? (
        <View style={styles.acceptedBanner}>
          <Text style={styles.acceptedBannerText}>
            You accepted these terms on{' '}
            {new Date(acceptedAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
            . You may proceed to link Stripe or PayPal.
          </Text>
        </View>
      ) : (
        <Text style={styles.intro}>
          Read each item below and check every box. One acceptance applies to both
          linking Stripe and linking PayPal.
        </Text>
      )}

      <ScrollView
        style={styles.summaryScroll}
        nestedScrollEnabled
        showsVerticalScrollIndicator
      >
        <Text style={styles.summaryText}>{MERCHANT_PAYMENT_ADDENDUM_SUMMARY}</Text>
      </ScrollView>

      <View style={styles.checkboxes}>
        {MERCHANT_TERMS_CHECKBOXES.map((item) => {
          const isChecked = checked[item.id];
          return (
            <TouchableOpacity
              key={item.id}
              style={styles.checkboxRow}
              onPress={() => toggle(item.id)}
              activeOpacity={0.7}
              disabled={alreadyAccepted}
            >
              <View
                style={[styles.checkbox, isChecked && styles.checkboxChecked]}
              >
                {isChecked ? <Text style={styles.checkmark}>✓</Text> : null}
              </View>
              <Text style={styles.checkboxLabel}>
                {item.label}
                {item.linkLabel && item.linkUrl ? (
                  <Text
                    style={styles.link}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      openUrl(item.linkUrl!);
                    }}
                  >
                    {item.linkLabel}
                  </Text>
                ) : null}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {persistError ? (
        <Text style={styles.errorText}>{persistError}</Text>
      ) : null}
      {saving ? (
        <Text style={styles.savingText}>Saving your acceptance…</Text>
      ) : null}
      {!alreadyAccepted && !allChecked ? (
        <Text style={styles.hint}>
          {REQUIRED_MERCHANT_TERMS_IDS.filter((id) => !checked[id]).length} item
          {REQUIRED_MERCHANT_TERMS_IDS.filter((id) => !checked[id]).length === 1
            ? ''
            : 's'}{' '}
          remaining
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 4,
  },
  version: {
    fontSize: 12,
    color: '#6c757d',
    marginBottom: 12,
  },
  intro: {
    fontSize: 14,
    color: '#495057',
    lineHeight: 20,
    marginBottom: 12,
  },
  acceptedBanner: {
    backgroundColor: '#d4edda',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  acceptedBannerText: {
    fontSize: 14,
    color: '#155724',
    lineHeight: 20,
  },
  summaryScroll: {
    maxHeight: 160,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  summaryText: {
    fontSize: 13,
    color: '#495057',
    lineHeight: 19,
  },
  checkboxes: {
    gap: 4,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#adb5bd',
    marginRight: 10,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  checkboxChecked: {
    backgroundColor: '#635bff',
    borderColor: '#635bff',
  },
  checkmark: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  checkboxLabel: {
    flex: 1,
    fontSize: 14,
    color: '#343a40',
    lineHeight: 20,
  },
  link: {
    color: '#635bff',
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  errorText: {
    color: '#dc3545',
    fontSize: 13,
    marginTop: 8,
  },
  savingText: {
    color: '#6c757d',
    fontSize: 13,
    marginTop: 8,
    fontStyle: 'italic',
  },
  hint: {
    color: '#856404',
    fontSize: 13,
    marginTop: 8,
    fontWeight: '500',
  },
});

export default MerchantPaymentTermsAgreement;
